import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSubscription } from '../hooks/useSubscription';
import PlanGrid from '../components/subscription/PlanGrid';
import CheckoutModal from '../components/subscription/CheckoutModal';
import PayPerVideoPricing from '../components/pricing/PayPerVideoPricing';
import { loadStripe } from '@stripe/stripe-js';

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { plans, loading, error, loadPlans } = useSubscription();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);

  // Load plans
  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Init Stripe
  useEffect(() => {
    const initStripe = async () => {
      try {
        const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (publishableKey) {
          const stripeInstance = await loadStripe(publishableKey);
          setStripe(stripeInstance);
        }
      } finally {
        setStripeLoading(false);
      }
    };

    initStripe();
  }, []);

  const handlePlanSelect = (plan) => {
    if (!user) {
      navigate('/login', { state: { from: location.pathname || '/pricing' } });
      return;
    }

    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  const handleCheckoutSuccess = (result) => {
    setShowCheckout(false);
    setSelectedPlan(null);

    navigate('/account/subscription', {
      state: {
        message:
          result.type === 'free'
            ? 'Free subscription started successfully!'
            : 'Payment successful! Your subscription is now active.',
      },
    });
  };

  const handleCheckoutClose = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
                {!user && (
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-brand-red font-semibold hover:text-red-700 transition-colors"
            >
              Login
            </button>
          )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Enhance Your Experience
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
            Start for free, pay only when you need a video processed. Add a subscription
            for more storage and free monthly credits.
          </p>
        </div>

        {/* Two-column layout: pay-per-video + subscriptions */}
        <section className="grid gap-8 lg:grid-cols-[240px,minmax(0,1fr)] items-start">
          {/* Left: Pay-per-video (compact card) */}
          <div>
            <PayPerVideoPricing />
          </div>

          {/* Right: Subscriptions */}
          <div>
            {/* <div className="mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Optional Subscriptions
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                Ideal if you use Akture regularly and want extra storage plus free monthly
                video credits. You can still use pay-per-video anytime.
              </p>
            </div> */}

            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <PlanGrid
                plans={plans}
                onPlanSelect={handlePlanSelect}
                loading={loading}
                error={error}
              />
            </div>
          </div>
        </section>
      </main>

      {/* Checkout Modal */}
      {selectedPlan && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={handleCheckoutClose}
          plan={selectedPlan}
          stripe={stripe}
          onSuccess={handleCheckoutSuccess}
          onError={() => {}}
          actionType="new"
        />
      )}

      {/* Stripe Loading Overlay */}
      {stripeLoading && (
        <div className="fixed inset-0 z-50 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Initializing payment system...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
